import { alpha, Fab, Tooltip } from '@mui/material';
import { FaScrewdriverWrench } from 'react-icons/fa6';
import { useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import FilesView from 'views/file-management/FilesView';
import ModelsView from 'views/prompts-models/ModelsView';
import ModelSpecificationsView from 'views/prompts-models/ModelSpecificationsView';
import PromptsView from 'views/prompts/PromptsView';
import PythonFunctionsView from 'views/python-functions/PythonFunctionsView';
import SecretsView from 'views/secrets/SecretsView';
import SettingsView from 'views/settings/SettingsView';
import AccountView from 'views/account/AccountView';
import UsersView from 'views/users/UsersView';
import { SettingsHub } from 'components/settingsHub/SettingsHub';
import { useSettingsNav } from 'components/settingsHub/useSettingsNav';
import { SettingsSectionKey } from 'components/settingsHub/sections';

const renderSection = (section: SettingsSectionKey, projectId?: string) => {
  switch (section) {
    case 'settings':
      return <SettingsView />;
    case 'account':
      return <AccountView />;
    case 'users':
      return <UsersView />;
    case 'models':
      return <ModelsView />;
    case 'model-specifications':
      return <ModelSpecificationsView />;
    case 'prompts':
      return <PromptsView projectId={projectId} />;
    case 'python-functions':
      return <PythonFunctionsView projectId={projectId} />;
    case 'files':
      return <FilesView projectId={projectId} />;
    case 'secrets':
      return <SecretsView projectId={projectId} />;
  }
};

const ConfigSpeedDial = () => {
  const { projectId } = useParams();
  const { section, open, close } = useSettingsNav();

  const lastSectionRef = useRef<SettingsSectionKey>('settings');

  useEffect(() => {
    if (section) {
      lastSectionRef.current = section;
    }
  }, [section]);

  const selectSection = useCallback(
    (next: SettingsSectionKey) => {
      lastSectionRef.current = next;
      open(next);
    },
    [open],
  );

  return (
    <>
      <Tooltip title="Settings" placement="left">
        <Fab
          aria-label="Open settings"
          onClick={() => selectSection(lastSectionRef.current)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1100,
            width: 64,
            height: 64,
            borderRadius: '20px',
            backgroundColor: 'accent.main',
            color: 'accent.contrastText',
            transition: (theme) => theme.transitions.create(['transform', 'box-shadow']),
            boxShadow: (theme) => `0 12px 30px -10px ${alpha(theme.palette.accent.main, 0.55)}`,
            '&:hover': {
              backgroundColor: 'accent.main',
              transform: 'translateY(-2px)',
              boxShadow: (theme) => `0 18px 40px -10px ${alpha(theme.palette.accent.main, 0.7)}`,
            },
          }}
        >
          <FaScrewdriverWrench size={24} />
        </Fab>
      </Tooltip>

      {section && (
        <SettingsHub section={section} onSelectSection={selectSection} onClose={close}>
          {renderSection(section, projectId)}
        </SettingsHub>
      )}
    </>
  );
};

export default ConfigSpeedDial;
